// The offline status change, Move and Relist (BL-2448, ADR 066 — Offline Mode § 3 and § 4.4): the
// writes the offline status menu and ⋯ menu send become one change-queue entry each (through
// `buildQueuedEntry` only), replayed through the online door, and the device's copy shows them.
// Each answer is the online route's own DTO. Any other batch or placement fails as a dropped connection.
import { buildQueuedEntry, isRecord, type ChangeQueueJson, type JsonObject } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import type { OfflineNewLotPlacement } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-new-lot';
import {
  applyLotMoveOverlay,
  applyLotStatusOverlay,
  buildOfflineRelistCopy,
  type OfflineStatusTarget,
} from '@auctogon/admin-isomorphic/isomorphic/offline/offline-lot-placement';

import type { OfflineStore } from '../../gateways/native-device';
import type { OfflineRequest } from './create-offline-transport';
import { NOT_FOUND } from './offline-answers';
import { lotWriteHandlers } from './offline-write-handlers';
import { commitLocalWrite, isQueuedNewLot, labelOf, pendingCreateOf, unqueueable, withQueue, type WriteContext } from './offline-write-support';

const STATUS_TARGETS: ReadonlySet<string> = new Set<OfflineStatusTarget>(['draft', 'ready']);

/** A synced auction as a placement target, or null when the device does not hold it. */
async function placementOf(store: OfflineStore, auctionId: string): Promise<OfflineNewLotPlacement | null> {
  const record = await store.records.get({ collection: 'auctions', key: auctionId });
  if (!record) return null;
  return { auctionId, auctionName: record.listRow.title ?? null, auctionTimeZone: record.auction.chrome?.zone ?? null };
}

/** The one lot a Draft ↔ Upcoming batch names, and its target, or null for any other batch. */
function statusChangeOf(body: unknown): { lotId: string; target: OfflineStatusTarget } | null {
  if (!isRecord(body) || !Array.isArray(body.recordIds) || body.recordIds.length !== 1) return null;
  const [lotId] = body.recordIds;
  const action = body.action;
  if (typeof lotId !== 'string' || !isRecord(action) || action.type !== 'set-status') return null;
  return typeof action.target === 'string' && STATUS_TARGETS.has(action.target) ? { lotId, target: action.target as OfflineStatusTarget } : null;
}

async function queueStatus({ req, store, queue }: WriteContext) {
  const change = statusChangeOf(req.body);
  if (!change) return unqueueable();
  const lot = await store.records.get({ collection: 'lots', key: change.lotId });
  if (!lot) return NOT_FOUND;
  const createId = await pendingCreateOf(store, change.lotId);
  const next = applyLotStatusOverlay({ lot, target: change.target });
  const entry = buildQueuedEntry({
    id: globalThis.crypto.randomUUID(),
    op: 'lot.status',
    // Replayed as sent: one id, through the transition service's own batch door (ADR 033).
    call: { params: {}, query: null, body: req.body as ChangeQueueJson },
    label: labelOf(lot, await isQueuedNewLot(store, change.lotId)),
    lotId: change.lotId,
    dependsOn: createId,
  });
  await commitLocalWrite({ store, queue, entries: [entry], lot: { key: change.lotId, value: next, parentKey: next.detail.auctionId } });
  return { kind: 'ok' as const, data: { action: 'set-status' as const, affected: 1 } };
}

async function queueMove({ req, store, queue }: WriteContext, body: JsonObject) {
  const lotId = req.params.id;
  const target = body.moveToAuctionId;
  if (!lotId || (target !== null && typeof target !== 'string')) return unqueueable();
  const lot = await store.records.get({ collection: 'lots', key: lotId });
  if (!lot) return NOT_FOUND;
  const placement = target === null ? null : await placementOf(store, target);
  if (target !== null && !placement) return NOT_FOUND;
  const createId = await pendingCreateOf(store, lotId);
  const next = applyLotMoveOverlay({ lot, placement });
  const entry = buildQueuedEntry({
    id: globalThis.crypto.randomUUID(),
    op: 'lot.move',
    call: { params: { id: lotId }, query: null, body: { moveToAuctionId: target } },
    label: labelOf(lot, await isQueuedNewLot(store, lotId)),
    lotId,
    dependsOn: createId,
    // A discarded move brings the lot back to this auction on the device (a re-download).
    leftAuctionId: lot.detail.auctionId,
  });
  await commitLocalWrite({ store, queue, entries: [entry], lot: { key: lotId, value: next, parentKey: placement?.auctionId ?? null } });
  return { kind: 'ok' as const, data: { id: lotId } };
}

/** The one source lot a relist names, or null for anything else (several lots, a create). */
function relistSourceOf(body: JsonObject): string | null {
  const items = Array.isArray(body.items) ? body.items : [];
  const [item] = items;
  if (items.length !== 1 || !isRecord(item) || typeof item.sourceLotId !== 'string' || 'newLot' in item) return null;
  return item.sourceLotId;
}

async function queueRelist({ store, queue }: WriteContext, body: JsonObject) {
  const sourceLotId = relistSourceOf(body);
  const toInventory = body.targetKind === 'inventory';
  if (!sourceLotId || typeof body.targetId !== 'string' || (!toInventory && body.targetKind !== 'auction')) return unqueueable();
  const source = await store.records.get({ collection: 'lots', key: sourceLotId });
  if (!source) return NOT_FOUND;
  const placement = toInventory ? null : await placementOf(store, body.targetId);
  if (!toInventory && !placement) return NOT_FOUND;
  // The copy's client id is the entry's id and the relist's op id, so a resend answers this copy (AC 2).
  const clientId = globalThis.crypto.randomUUID();
  const copy = buildOfflineRelistCopy({ source, clientId, placement, at: new Date().toISOString() });
  const sourceCreateId = await pendingCreateOf(store, sourceLotId);
  const entry = buildQueuedEntry({
    id: clientId,
    op: 'lot.relist',
    call: { params: {}, query: null, body: { ...body, items: [{ sourceLotId, clientOpId: clientId }] } as ChangeQueueJson },
    // Filed under the SOURCE lot, so the relist bounds that lot's edit runs (an edit after it never
    // merges with one before it and lands ahead of the copy). The copy's own entries wait on it.
    label: labelOf(source, await isQueuedNewLot(store, sourceLotId)),
    lotId: sourceLotId,
    dependsOn: sourceCreateId,
  });
  await commitLocalWrite({ store, queue, entries: [entry], lot: { key: clientId, value: copy, parentKey: placement?.auctionId ?? null } });
  return { kind: 'ok' as const, data: { placed: [{ id: sourceLotId, lotId: clientId, verb: 'created' as const }], lots: [] } };
}

/** A PATCH carrying `moveToAuctionId` is a Move; any other PATCH is a Save (BL-2431). */
function patchLot(req: OfflineRequest) {
  const body = req.body;
  if (isRecord(body) && 'moveToAuctionId' in body) return withQueue(req, (ctx) => queueMove(ctx, body));
  return lotWriteHandlers['lots.id.index:PATCH'](req);
}

/** A placement of one existing lot is a Relist; a new lot is a create (BL-2431). */
function placeLot(req: OfflineRequest) {
  const body = req.body;
  if (isRecord(body) && Array.isArray(body.items) && body.items.some((item) => isRecord(item) && 'sourceLotId' in item)) {
    return withQueue(req, (ctx) => queueRelist(ctx, body));
  }
  return lotWriteHandlers['lots.placeInAuction:POST'](req);
}

/** The lot placement writes, keyed `"<registry key>:<VERB>"`; typed by `OfflineHandlers` where composed. */
export const lotPlacementHandlers = {
  'lots.batch:POST': (req: OfflineRequest) => withQueue(req, queueStatus),
  'lots.id.index:PATCH': patchLot,
  'lots.placeInAuction:POST': placeLot,
};
